import { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ShieldCheck, Trash2 } from 'lucide-react';
import { Header } from '@core/components/Header';
import { Card } from '@core/components/Card';
import { Button } from '@core/components/Button';
import { Checkbox } from '@core/components/Checkbox';
import { WebPushSettings } from '@core/components/WebPush';
import { useProfile } from '@core/contexts/ProfileContext';
import { coreRuntime } from '@core/runtime';
import { deleteUserAccount } from '@core/api/consent';
import { erasureDoneText, erasureErrorText, markDataErased } from '@core/utils/dataErasure';
import { pointsWord } from '@core/utils/redemptionCancel';

/**
 * «Мои данные» (K-025, 152-ФЗ ст. 21) — один экран на оба входа: что хранится, что удалится, что останется по
 * закону, и кнопка «Удалить мои данные» с подтверждением. После удаления — финальный экран без запросов к
 * серверу; вход завершает сессию по-своему (PWA — на вход по номеру, мини-апп — закрывается).
 */
export function MyDataPage() {
  const { user } = useProfile();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [understood, setUnderstood] = useState(false);
  const [busy, setBusy] = useState(false);
  const [doneText, setDoneText] = useState<string | null>(null);

  if (doneText) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center bg-page text-text-main">
        <ShieldCheck size={48} className="text-primary mb-4" />
        <h1 className="text-xl font-bold mb-3">Данные удалены</h1>
        <p className="text-text-muted">{doneText}</p>
      </div>
    );
  }

  const balance = user?.balance ?? 0;

  const handleDelete = async () => {
    if (!user || busy) return;
    setBusy(true);
    try {
      const result = await deleteUserAccount(user.id);
      markDataErased();
      setDoneText(erasureDoneText(result));
      const rt = coreRuntime();
      // Даём прочитать итог, затем вход завершает сессию (мини-апп закрывается, PWA — экран входа).
      setTimeout(() => rt.afterDataErased?.(), 4000);
    } catch (err) {
      toast.error(erasureErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-page to-page/95">
      <Header title="Мои данные" showBack showNotifications={false} />
      <div className="p-4 space-y-4 text-text-main">
        {/* PWA: «Уведомления в браузере» (в мини-аппе Telegram не показывается) */}
        <WebPushSettings />

        <Card>
          <h2 className="font-bold mb-2">Что мы храним</h2>
          <p className="text-sm text-text-muted">
            Имя и username из Telegram, номер телефона, историю баллов и покупок в заведении, согласия на обработку
            данных и рассылку. Подробнее — в{' '}
            <Link to="/legal/privacy-policy" className="text-primary underline">
              политике конфиденциальности
            </Link>
            .
          </p>
        </Card>

        <Card>
          <h2 className="font-bold mb-2">Удаление данных</h2>
          <ul className="text-sm text-text-muted list-disc pl-5 space-y-1">
            <li>Имя, телефон, Telegram, дата рождения, теги и история уведомлений удаляются.</li>
            <li>
              Баллы сгорают
              {balance > 0 ? ` (сейчас у вас ${balance} ${pointsWord(balance)})` : ''}, незавершённые награды и
              подарки отменяются с возвратом.
            </li>
            <li>
              По закону остаются обезличенные записи: история начислений и покупок (учёт), журнал согласий
              (доказательство согласия и отзыва) — без вашего имени и контактов.
            </li>
            <li>
              Если вы тратили баллы, мы сохраним зашифрованную отметку без номера и Telegram ID — чтобы при новой
              регистрации не начислять приветственный бонус повторно.
            </li>
            <li>Вернуть удалённые данные нельзя. Новая регистрация — новая карточка.</li>
          </ul>
        </Card>

        {!confirmOpen ? (
          <Button variant="secondary" fullWidth onClick={() => setConfirmOpen(true)} disabled={!user}>
            <span className="inline-flex items-center gap-2 text-red-600">
              <Trash2 size={18} /> Удалить мои данные
            </span>
          </Button>
        ) : (
          <Card>
            <label className="flex items-start gap-3 text-sm">
              <Checkbox checked={understood} onChange={(e) => setUnderstood(e.target.checked)} />
              <span>
                Понимаю: данные удалятся без возможности восстановления
                {balance > 0 ? `, ${balance} ${pointsWord(balance)} сгорят` : ''}.
              </span>
            </label>
            <div className="flex gap-3 mt-4">
              <Button variant="secondary" fullWidth onClick={() => setConfirmOpen(false)} disabled={busy}>
                Отмена
              </Button>
              <Button fullWidth onClick={() => void handleDelete()} disabled={!understood || busy}>
                {busy ? 'Удаляем…' : 'Удалить'}
              </Button>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
